import { useLedgerEnd } from '@hooks/queries/ledger/useLedgerEnd';
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
  QueryFunctionContext,
  QueryKey,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParties } from '../../contexts/PartiesContext';

type LedgerQueryFunction<TData, TQueryKey extends QueryKey> = (
  context: QueryFunctionContext<TQueryKey>,
  offset: number,
) => TData | Promise<TData>;

interface UseLedgerDependentOptions<TQueryFnData, TError, TData, TQueryKey extends QueryKey>
  extends Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'> {
  queryFn: LedgerQueryFunction<TQueryFnData, TQueryKey>;
}

export function useQueryWithOffset<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseLedgerDependentOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> {
  const queryClient = useQueryClient();
  const ledgerEndQuery = useLedgerEnd();
  const offsetHeartbeat = ledgerEndQuery.data;
  const { party } = useParties();
  const isLedgerReady = ledgerEndQuery.isSuccess && offsetHeartbeat !== undefined;
  const queryFnWrapper = async (context: QueryFunctionContext<TQueryKey>) => {
    // This check is double-safety, but the 'enabled' prop below handles the primary logic
    if (!isLedgerReady) {
      throw new Error('Ledger offset not ready');
    }
    return options.queryFn(context, offsetHeartbeat);
  };
  const queryKeys = options.queryKey || [];
  const queryKeysWithCommonKey = [...queryKeys, party];
  const query = useQuery({
    ...options,
    queryKey: queryKeysWithCommonKey as unknown as TQueryKey,
    queryFn: queryFnWrapper,
    refetchInterval: false,
    enabled: options.enabled !== false && offsetHeartbeat !== undefined,
  });
  const { refetch } = query;
  useEffect(() => {
    if (isLedgerReady) {
      refetch().catch(() => {
        throw new Error('Refetch failed');
      });
    }
  }, [isLedgerReady, offsetHeartbeat, queryClient, refetch]);

  return query;
}
